import { type AreaComponent, areaComponentSchema } from '@repo/api-contracts';
import { type KeyboardEvent, type ReactElement, type ReactNode, useId, useState } from 'react';
import { formatAreaMm, koreanMessageOr } from '@/shared/lib';
import { Input } from '@/shared/ui';
import { componentFieldHint } from '../model/describe-edit-area-issue';
import { EditorNumberField } from './editor-number-field';
import { FieldAlert } from './field-alert';
import { type HoleToolControl, ShapeHoleList } from './shape-hole-list';

type FieldKey = 'name' | 'width' | 'height';

interface FieldError {
  readonly field: FieldKey;
  readonly message: string;
}

/** 적용할 값이 저장된 값과 같은가(키 순서와 무관하게 이름·크기로 본다 — 모양은 폼이 바꾸지 않는다). */
const sameComponent = (a: AreaComponent, b: AreaComponent): boolean =>
  a.name === b.name && a.widthMm === b.widthMm && a.heightMm === b.heightMm;

interface ComponentFormProps {
  readonly component: AreaComponent;
  /** 적용되면 null, 거부되면 그 사유. 사유는 고친 칸 아래에 그대로 보여준다. */
  readonly onApply: (component: AreaComponent) => string | null;
  readonly onDirtyChange: (dirty: boolean) => void;
  readonly onRemoveHole: (index: number) => void;
  /**
   * 인쇄 제외 영역 칸(도구 줄·고른 구멍). 넘기면 모양과 무관하게 칸을 보이고(사각형·원형도 구멍을 그리면 자유 다각형이 된다),
   * 넘기지 않으면 칸을 숨긴다.
   */
  readonly holeControl?: {
    readonly selectedIndex: number | null;
    readonly onSelect: (index: number) => void;
    readonly tools: HoleToolControl;
  };
  readonly disabled: boolean;
  /** '인쇄 영역 수정' 제목 줄 오른쪽 끝에 둘 동작(예: [인쇄 영역 삭제]). */
  readonly headerAction?: ReactNode;
}

/**
 * 인쇄 영역 이름·크기. 칸은 Enter 를 누르거나 칸을 떠나면 바로 적용하고(적용 버튼 없음), Esc 는 저장된 값으로 되돌린다.
 * 거부되면 친 글자를 남기고 그 칸 아래에 사유를 보인다. 모양은 그릴 때 도구로만 정한다 — 바꾸려면 지우고 다시 그린다.
 * 자유 다각형은 크기가 점으로 정해져 읽기 전용이다(크기는 사진 위 핸들로 바꾼다). 인쇄 제외 영역 칸은 holeControl 로 둔다.
 */
export const ComponentForm = ({
  component,
  onApply,
  onDirtyChange,
  onRemoveHole,
  holeControl,
  disabled,
  headerAction,
}: ComponentFormProps): ReactElement => {
  const storedWidth = formatAreaMm(component.widthMm);
  const storedHeight = formatAreaMm(component.heightMm);
  const [name, setName] = useState(component.name);
  // 저장된 치수는 소수 첫째 자리로 보인다. 고치지 않은 칸은 적용할 때 저장된 값을 그대로 쓴다(표시 글자를 되돌려 저장하지 않는다).
  const [width, setWidth] = useState(storedWidth);
  const [height, setHeight] = useState(storedHeight);
  const [widthTouched, setWidthTouched] = useState(false);
  const [heightTouched, setHeightTouched] = useState(false);
  // 고쳤지만 아직 적용되지 않은 입력이 있는가 — Enter·칸 떠나기가 이때만 적용을 시도한다.
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<FieldError | null>(null);
  // 마지막으로 칸에 맞춘 저장값. 적용(이 폼이나 사진 위 조작)으로 저장값이 바뀌면 다시 만들지 않고 칸만 맞춘다 —
  // 폼을 다시 만들면 Tab 으로 옮겨 간 칸의 포커스가 사라진다.
  const [synced, setSynced] = useState(component);
  if (synced !== component) {
    setSynced(component);
    if (!pending) {
      setName(component.name);
      setWidth(storedWidth);
      setHeight(storedHeight);
      setWidthTouched(false);
      setHeightTouched(false);
      setError(null);
    } else {
      // 고치는 중인 칸은 친 글자를 남기고, 고치지 않은 칸만 새 저장값으로 맞춘다.
      const nameUntouched = name === synced.name;
      if (nameUntouched) setName(component.name);
      if (!widthTouched) setWidth(storedWidth);
      if (!heightTouched) setHeight(storedHeight);
      if (nameUntouched && !widthTouched && !heightTouched) setPending(false);
    }
  }
  const nameId = useId();
  const polygon = component.shape?.type === 'polygon' ? component.shape : null;
  const markEdited = (): void => {
    setPending(true);
    onDirtyChange(true);
  };
  const settle = (): void => {
    setError(null);
    setPending(false);
    onDirtyChange(false);
  };
  /** 지금 입력으로 적용한다. 저장된 값과 같으면 적용하지 않고 입력 중 표시만 지운다. */
  const commit = (field: FieldKey): void => {
    if (disabled) return;
    const parsed = areaComponentSchema.safeParse({
      ...component,
      name,
      widthMm: widthTouched ? Number(width) : component.widthMm,
      heightMm: heightTouched ? Number(height) : component.heightMm,
    });
    if (!parsed.success) {
      // zod 기본 영어 문구는 그 칸의 한국어 안내로 바꾸고, 같은 안내는 한 번만 보인다.
      const messages = parsed.error.issues.map((issue) =>
        koreanMessageOr(issue.message, componentFieldHint(issue.path[0])),
      );
      setError({ field, message: [...new Set(messages)].join(' · ') });
      return;
    }
    if (sameComponent(parsed.data, component)) {
      settle();
      return;
    }
    const rejection = onApply(parsed.data);
    if (rejection !== null) {
      setError({ field, message: rejection });
      return;
    }
    settle();
  };
  const revert = (): void => {
    setName(component.name);
    setWidth(storedWidth);
    setHeight(storedHeight);
    setWidthTouched(false);
    setHeightTouched(false);
    settle();
  };
  const keyHandler =
    (field: FieldKey) =>
    (event: KeyboardEvent<HTMLInputElement>): void => {
      if (event.key === 'Enter') {
        event.preventDefault();
        if (pending) commit(field);
      } else if (event.key === 'Escape') {
        event.preventDefault();
        revert();
      }
    };
  const blurHandler = (field: FieldKey) => (): void => {
    if (pending) commit(field);
  };
  const alertFor = (field: FieldKey): string | null =>
    error?.field === field ? error.message : null;
  return (
    <form aria-label="인쇄 영역 입력" noValidate onSubmit={(event) => event.preventDefault()}>
      <fieldset disabled={disabled} aria-label="인쇄 영역 수정" className="space-y-3">
        <legend className="flex w-full items-center justify-between gap-2 font-medium">
          <span>인쇄 영역 수정</span>
          {headerAction}
        </legend>
        <label htmlFor={nameId} className="grid gap-1 text-sm">
          인쇄 영역 이름
          <Input
            id={nameId}
            value={name}
            onChange={(event) => {
              setName(event.target.value);
              markEdited();
            }}
            onKeyDown={keyHandler('name')}
            onBlur={blurHandler('name')}
          />
        </label>
        <FieldAlert message={alertFor('name')} />
        {component.shape?.type === 'ellipse' ? (
          <p className="text-sm text-muted-foreground">
            원형은 가로·세로에 내접하는 타원입니다. 지름이 같으면 원입니다.
          </p>
        ) : null}
        <EditorNumberField
          label="인쇄 가로 (mm)"
          value={width}
          readOnly={polygon !== null}
          onChange={(value) => {
            setWidth(value);
            setWidthTouched(true);
            markEdited();
          }}
          onKeyDown={keyHandler('width')}
          onBlur={blurHandler('width')}
        />
        <FieldAlert message={alertFor('width')} />
        <EditorNumberField
          label="인쇄 세로 (mm)"
          value={height}
          readOnly={polygon !== null}
          onChange={(value) => {
            setHeight(value);
            setHeightTouched(true);
            markEdited();
          }}
          onKeyDown={keyHandler('height')}
          onBlur={blurHandler('height')}
        />
        <FieldAlert message={alertFor('height')} />
        {holeControl === undefined ? null : (
          <ShapeHoleList
            holes={polygon?.holes ?? []}
            selectedIndex={holeControl.selectedIndex}
            onSelect={holeControl.onSelect}
            tools={holeControl.tools}
            onRemove={(index) => {
              // 고치는 중인 입력이 없으면 남은 미저장 표시를 지운다. 틀린 값을 고치는 중이면 표시와 친 글자를 그대로 둔다.
              if (!pending) onDirtyChange(false);
              onRemoveHole(index);
            }}
            disabled={disabled}
          />
        )}
      </fieldset>
    </form>
  );
};
